import React, { useState } from 'react';
import { fieldProps } from './missingFields';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import FileUpload from '../../../components/ui/FileUpload';
import jsPDF from 'jspdf';
import { isIndia } from '../../../utils/countries';
import { getCurrencySymbol, getCurrencyName, countryCurrencies } from '../../../utils/currencies';
import { validateFileUpload } from './FormValidation';
import { StepHeader, StepNote } from './StepHeader';
import LockedField from './LockedField';
import UdyamLookupPanel from './UdyamLookupPanel';
import DocumentReadPanel from './DocumentReadPanel';
import { useLocked } from '../hooks/useLocked';
import Icon from '../../../components/AppIcon';
import { registrationService, getErrorMessage } from '../../../services/apiService';

// Reads an uploaded signature (File/Blob) — or an existing data URL — as a data URL for jsPDF.
const readSignatureDataUrl = (sig) =>
  new Promise((resolve) => {
    if (!sig) return resolve(null);
    if (typeof sig === 'string' && sig.startsWith('data:')) return resolve(sig);
    if (sig instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(sig);
      return;
    }
    resolve(null);
  });

const CategorizationStep = ({
  formData,
  updateFormData,
  errors,
  onPreviewDocument,
  // Surepass MSME automation (Indian vendors in the registration flow)
  kycActive = false,
  sessionId,
  identity,
  kyc,
  locked,
  lockSources,
  onKycResult,
  uploadedFiles,
}) => {
  const [showMSMEDeclaration, setShowMSMEDeclaration] = useState(false);
  // Udyam-certificate OCR cross-check (item 5): read the classification date off the uploaded
  // certificate, compare it to the authoritative date, and say whether the certificate is current.
  const [certCheck, setCertCheck] = useState(null); // { date_match, recent, outdated, ocr, expected_classification_date, expected_source }
  const [certChecking, setCertChecking] = useState(false);
  const [certError, setCertError] = useState(null);
  const lock = useLocked(locked, lockSources, 4);
  // The certificate is already read once for its classification date, so the fill is offered as a
  // button: a second read only happens if the vendor wants the form filled from it.
  const canRead = Boolean(sessionId) && kyc?.enabled !== false;
  const [certFile, setCertFile] = useState(null);
  const applyRead = (applied) => updateFormData(Object.fromEntries(applied.map((a) => [a.key, a.value])));

  const runCertOcr = async (file) => {
    if (!file || !sessionId) return;
    setCertChecking(true);
    setCertError(null);
    setCertCheck(null);
    try {
      const res = await registrationService.udyamCertOcr(sessionId, file, formData.classificationDate || null);
      setCertCheck(res?.data || null);
    } catch (e) {
      setCertError(getErrorMessage(e));
    } finally {
      setCertChecking(false);
    }
  };

  const handleCertUpload = (file) => {
    handleFileUpload('msmeCertificate', file);
    if (file) runCertOcr(file);
    else { setCertCheck(null); setCertError(null); }
  };

  const proceedAsNonMsme = () => {
    setCertCheck(null);
    setCertError(null);
    handleMSMEChange('not-registered');
    setShowMSMEDeclaration(true);
  };
  const udyamVerified = Boolean(identity?.udyam_verified_at && identity?.udyam);
  // The portal could not help and the vendor kept the number as typed: the MSME fields are theirs to fill.
  const udyamManual = !udyamVerified && Boolean(identity?.udyam_manual);
  const udyamLookupOn = kycActive && kyc?.enabled !== false && kyc?.features?.udyam !== 'unavailable';
  const fetchedCertificate = udyamVerified && uploadedFiles?.MSME_CERTIFICATE && identity?.udyam?.certificate_staged;
  const msmeHint = identity?.msme_hint;

  // Supplier Type removed as per new requirement

  // Updated supplier groups as per requirements
  const supplierGroups = [
    { value: 'oem-customer', label: 'OEM-Customer Referred Supplier' },
    { value: 'odm-amber', label: 'ODM-Amber Developed Supplier' }
  ];

  // Updated supplier categories as per requirements
  const supplierCategories = [
    { value: 'rw', label: 'RW-Raw Material' },
    { value: 'pk', label: 'PK-Packaging' },
    { value: 'consumables', label: 'Consumables' },
    { value: 'others', label: 'Others' }
  ];

  // Commodity codes removed - now collected in approval questionnaire

  // MSME categories
  const msmeCategories = [
    { value: 'micro', label: 'Micro' },
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' }
  ];

  // Vendor types for MSME classification
  const vendorTypes = [
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'trading', label: 'Trading' },
    { value: 'service', label: 'Service' }
  ];

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleFileUpload = (field, file) => {
    // FileUpload component now handles validation internally
    updateFormData({ [field]: file });
  };

  const handleRemoveFile = (field) => {
    updateFormData({ [field]: null });
  };

  const handleMSMEChange = (value) => {
    updateFormData({ 
      msmeStatus: value,
      msmeDeclaration: false,
      msmeCertificate: null,
      msmeCategory: '',
      msmeNumber: ''
    });
  };

  const generateMSMEDeclarationPDF = async () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-GB');
    const formattedTime = currentDate.toLocaleTimeString('en-GB');

    // Create new PDF document
    const doc = new jsPDF();

    // Set font styles
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');

    // Header: Company name only (remove person-in-charge name)
    doc.text(`${formData.companyName}`, 20, 30);

    // Title
    doc.setFontSize(14);
    doc.text('DECLARATION OF REGISTRATION IN NON MSME', 20, 50);

    // Reset font for body text
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');

    // Updated MSME declaration text
    const declarationText = `We, ${formData.companyName}, a company incorporated under the laws of India and having its registered office at ${formData.registeredAddress}, ${formData.registeredCity}, ${formData.registeredState}, ${formData.registeredCountry}, do hereby declare that:

1. Our company is NOT registered under the Micro, Small and Medium Enterprises Development Act, 2006 (MSME Act) as on the date of this declaration.

2. We understand that MSME registration provides various benefits and incentives under government schemes, but we have chosen not to register under the MSME Act.

3. This declaration is made voluntarily and we are aware of the consequences of not being registered under the MSME Act.

4. We undertake to inform Amber Enterprises India Limited immediately if we register under the MSME Act in the future.`;

    // Split text to fit page width and render with dynamic spacing
    const pageHeight = (doc.internal.pageSize.getHeight && doc.internal.pageSize.getHeight()) || doc.internal.pageSize.height;
    const contentX = 20;
    const contentWidth = 170;
    const bodyStartY = 70;
    const splitText = doc.splitTextToSize(declarationText, contentWidth);
    doc.text(splitText, contentX, bodyStartY);
    const bodyTextDims = doc.getTextDimensions(splitText);
    let cursorY = bodyStartY + (bodyTextDims && bodyTextDims.h ? bodyTextDims.h : 0) + 12;

    // Signature section
    doc.text('For and on behalf of', contentX, cursorY);
    cursorY += 8;
    // Embed the uploaded digital signature image (captured in the Company Information step), if any.
    const signatureDataUrl = await readSignatureDataUrl(formData.digitalSignature);
    if (signatureDataUrl) {
      try {
        const fmt = signatureDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(signatureDataUrl, fmt, contentX, cursorY, 45, 20);
        cursorY += 24;
      } catch (e) {
        // Unreadable image — fall back to the text-only signature block below.
      }
    }
    doc.setFont('helvetica', 'bold');
    doc.text(formData.contactPersonName, contentX, cursorY);
    cursorY += 10;
    doc.setFont('helvetica', 'normal');
    doc.text('(Digitally Signed)', contentX, cursorY);
    cursorY += 12;

    // Address
    const addressText = `${formData.registeredAddress}, ${formData.registeredCity}, ${formData.registeredState}, ${formData.registeredCountry}`;
    const splitAddress = doc.splitTextToSize(addressText, contentWidth);
    doc.text(splitAddress, contentX, cursorY);
    const addressDims = doc.getTextDimensions(splitAddress);
    cursorY += (addressDims && addressDims.h ? addressDims.h : 0) + 12;

    // Footer with timestamp (ensure it stays below content and within page)
    doc.setFontSize(10);
    const footerY = Math.min(cursorY + 10, pageHeight - 15);
    doc.text(`Document has been digitally signed with acceptance on Amber Compliance System at ${formattedTime} on ${formattedDate}`, contentX, footerY);

    // Prepare filename and Blob for backend upload
    const fileName = `MSME_Declaration_${formData.companyName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    const blob = doc.output('blob');
    const file = new File([blob], fileName, { type: 'application/pdf' });

    // Download for user and store for backend upload on submit
    doc.save(fileName);

    // Update form to indicate declaration was generated and attach file
    updateFormData({ msmeDeclaration: true, msmeDeclarationFile: file });
    setShowMSMEDeclaration(false);
  };

  // Check if the selected country is India
  const isIndianCountry = isIndia(formData.countryOrigin);

  // Get currency information based on country
  const currencySymbol = getCurrencySymbol(formData.countryOrigin);
  const currencyName = getCurrencyName(formData.countryOrigin);

  // Build comprehensive currency list (deduplicated by currency code)
  const currencyOptions = React.useMemo(() => {
    const seen = new Set();
    const options = [];
    for (const c of countryCurrencies) {
      if (!seen.has(c.code)) {
        seen.add(c.code);
        options.push({ value: c.code, label: `${c.code} - ${c.name}` });
      }
    }
    // Sort alphabetically by code for easier search
    options.sort((a, b) => a.value.localeCompare(b.value));
    return options;
  }, []);

  return (
    <div className="space-y-6">
      <StepHeader
        title={isIndianCountry && kycActive ? 'MSME & Turnover' : 'Supplier Categorization'}
        description={
          isIndianCountry && kycActive
            ? 'Your turnover band comes from GST; your MSME classification comes from the Udyam portal. Confirm what you see and fill the rest.'
            : 'Helps us route your registration to the right procurement team.'
        }
      >
        {!isIndianCountry && (
          <StepNote>Some fields only apply to Indian vendors and are hidden for your country.</StepNote>
        )}
        {isIndianCountry && kycActive && msmeHint?.exists && !udyamVerified && (
          <StepNote tone="success" icon="Factory">
            Government records show a Udyam (MSME) registration against your PAN — we pre-selected "MSME Registered" below.
          </StepNote>
        )}
      </StepHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Supplier Type removed */}

        {/* Supplier Group and Supplier Category fields removed as per requirements */}

            <Select
              label="Preferred Currency"
              description="Select your preferred currency for transactions"
              options={currencyOptions}
              value={formData.preferredCurrency}
              {...fieldProps('preferredCurrency')}
              onChange={(value) => handleInputChange('preferredCurrency', value)}
              error={errors.preferredCurrency}
              searchable
              required
            />

        {lock.isLocked('gstTurnoverSlab') && (
          <LockedField
            label="GST Turnover Slab"
            value={formData.gstTurnoverSlab}
            displayValue={`${String(formData.gstTurnoverSlab || '').replace(/^Slab:\s*/i, '')}${formData.gstTurnoverFy ? ` · FY ${formData.gstTurnoverFy}` : ''}`}
            source="gst"
            description="Aggregate turnover band reported on the GST portal"
          />
        )}

        {lock.isLocked('annualTurnover') ? (
          <LockedField
            label={`Annual Turnover (${currencySymbol})`}
            displayValue={new Intl.NumberFormat('en-IN').format(Number(formData.annualTurnover))}
            source="udyam"
            description="Exact turnover confirmed via Udyam OTP"
          />
        ) : (
          <Input
            label={`Annual Turnover (${currencySymbol})`}
            type="number"
            placeholder={`Enter annual turnover in ${currencyName}`}
            value={formData.annualTurnover}
            {...fieldProps('annualTurnover')}
            onChange={(e) => handleInputChange('annualTurnover', e.target.value)}
            error={errors.annualTurnover}
            description={lock.isLocked('gstTurnoverSlab') ? 'Optional — exact figure for the latest financial year (or confirm it below with a Udyam OTP)' : `Currency: ${currencyName} (${currencySymbol})`}
          />
        )}

        {/* Products/Services Offered removed */}
      </div>

      {/* MSME Status Section - Only for Indian vendors */}
      {isIndianCountry && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">MSME Status</h3>
          <p className="text-sm text-text-secondary mb-4">
            Micro, Small & Medium Enterprises (MSME) registration provides various benefits under government schemes.
          </p>

          <div className="space-y-4">
            <Select
              label="MSME Registration Status"
              options={[
                { value: 'registered', label: 'MSME Registered' },
                { value: 'not-registered', label: 'Not MSME Registered' }
              ]}
              value={formData.msmeStatus}
              {...fieldProps('msmeStatus')}
              onChange={handleMSMEChange}
              error={errors.msmeStatus}
              required
            />

            {formData.msmeStatus === 'registered' && (
              <div className="space-y-4">
                {udyamLookupOn && (
                  <div {...fieldProps('udyamVerified')} tabIndex={-1} className="space-y-4 outline-none">
                    <UdyamLookupPanel
                      sessionId={sessionId}
                      identity={identity}
                      kyc={kyc}
                      formData={formData}
                      onResult={onKycResult}
                      uploadedFiles={uploadedFiles}
                      onPreviewDocument={onPreviewDocument}
                    />
                    {!udyamVerified && !udyamManual && errors.udyamVerified && <p className="text-sm text-destructive">{errors.udyamVerified}</p>}
                  </div>
                )}

                {(!udyamLookupOn || udyamVerified || udyamManual) && (
                  <>
                    {lock.isLocked('msmeCategory') ? (
                      <LockedField label="MSME Category" displayValue={(msmeCategories.find((o) => o.value === formData.msmeCategory) || {}).label || formData.msmeCategory} source="udyam" required />
                    ) : (
                      <Select
                        label="MSME Category"
                        options={msmeCategories}
                        value={formData.msmeCategory}
                        {...fieldProps('msmeCategory')}
                        onChange={(value) => handleInputChange('msmeCategory', value)}
                        error={errors.msmeCategory}
                        required
                      />
                    )}

                    {lock.isLocked('msmeNumber') ? (
                      <LockedField label="UDYAM Registration Number" value={formData.msmeNumber} source="udyam" required />
                    ) : (
                      <Input
                        label="UDYAM Registration Number"
                        type="text"
                        placeholder="Enter UDYAM registration number (e.g., UDYAM-MH-00-1234567)"
                        value={formData.msmeNumber}
                        {...fieldProps('msmeNumber')}
                        onChange={(e) => handleInputChange('msmeNumber', e.target.value.toUpperCase())}
                        error={errors.msmeNumber}
                        required
                        description="Format: UDYAM-XX-00-XXXXXXX (e.g., UDYAM-MH-00-1234567)"
                      />
                    )}

                    {lock.isLocked('classificationYear') ? (
                      <LockedField label="Classification Year" value={formData.classificationYear} source="udyam" required />
                    ) : (
                      <Input
                        label="Classification Year"
                        type="number"
                        placeholder="Enter classification year (e.g., 2024)"
                        value={formData.classificationYear}
                        {...fieldProps('classificationYear')}
                        onChange={(e) => handleInputChange('classificationYear', e.target.value)}
                        error={errors.classificationYear}
                        min="2020"
                        max="2030"
                        required
                      />
                    )}

                    {lock.isLocked('classificationDate') ? (
                      <LockedField label="Classification Date" value={formData.classificationDate} source="udyam" required />
                    ) : (
                      <Input
                        label="Classification Date"
                        type="date"
                        value={formData.classificationDate}
                        {...fieldProps('classificationDate')}
                        onChange={(e) => handleInputChange('classificationDate', e.target.value)}
                        error={errors.classificationDate}
                        required
                      />
                    )}

                    {lock.isLocked('vendorType') ? (
                      <LockedField label="Vendor Type" displayValue={(vendorTypes.find((o) => o.value === formData.vendorType) || {}).label || formData.vendorType} source="udyam" />
                    ) : (
                      <Select
                        label="Vendor Type"
                        options={vendorTypes}
                        value={formData.vendorType}
                        {...fieldProps('vendorType')}
                        onChange={(value) => handleInputChange('vendorType', value)}
                        error={errors.vendorType}
                        placeholder="Select vendor type"
                        required
                      />
                    )}

                    {fetchedCertificate ? (
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-success/30 bg-success/5 px-4 py-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="FileCheck" size={18} className="text-success" />
                          <span className="font-medium text-foreground">MSME certificate</span>
                          <span className="text-text-secondary">fetched from the Udyam portal · {uploadedFiles.MSME_CERTIFICATE.file_name}</span>
                        </div>
                        <button type="button" onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.MSME_CERTIFICATE, 'MSME_CERTIFICATE')} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                          <Icon name="Eye" size={12} /> Preview
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <FileUpload
                          label="MSME Certificate"
                          accept=".pdf,.jpg,.jpeg,.png"
                          maxSize={4 * 1024 * 1024}
                          {...fieldProps('msmeCertificate')}
                          value={formData.msmeCertificate}
                          onChange={(file) => { handleCertUpload(file); setCertFile(file || null); }}
                          onRemove={() => { handleRemoveFile('msmeCertificate'); setCertCheck(null); setCertError(null); setCertFile(null); }}
                          onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'MSME_CERTIFICATE')}
                          error={errors.msmeCertificate}
                          required
                          description={udyamVerified ? 'The Udyam portal did not return a certificate — upload yours (Max 4MB - PDF, JPG, or PNG)' : 'Max 4MB - PDF, JPG, or PNG format'}
                        />

                        {certChecking && (
                          <p className="flex items-center gap-2 text-sm text-text-secondary">
                            <Icon name="Loader2" size={16} className="animate-spin text-primary" /> Reading the certificate and checking its dates…
                          </p>
                        )}
                        {canRead && certFile && !certChecking && (
                          <DocumentReadPanel
                            sessionId={sessionId}
                            kind="MSME_CERTIFICATE"
                            file={certFile}
                            autoRead={false}
                            onApplied={applyRead}
                            confirmedBy={formData.email || formData.directorEmail || ''}
                            onCleared={() => setCertFile(null)}
                          />
                        )}
                        {certError && (
                          <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
                            <Icon name="AlertCircle" size={16} /> {certError}
                          </p>
                        )}
                        {certCheck && !certChecking && (() => {
                          const problem = certCheck.date_match === false || certCheck.recent === false;
                          if (!problem) {
                            return (
                              <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-sm" role="status">
                                <Icon name="ShieldCheck" size={18} className="mt-0.5 flex-shrink-0 text-success" />
                                <div>
                                  <p className="font-medium text-foreground">Certificate dates verified</p>
                                  <p className="text-text-secondary">
                                    Classification date {certCheck.ocr?.classification_date || '—'} matches
                                    {certCheck.expected_source === 'api' ? ' the Udyam registry' : ' the date you entered'}
                                    {certCheck.ocr?.issue_date ? ` · registered on Udyam ${certCheck.ocr.issue_date}` : ''}.
                                  </p>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3" role="alert">
                              <div className="flex items-start gap-2">
                                <Icon name="AlertTriangle" size={18} className="mt-0.5 flex-shrink-0 text-warning" />
                                <div className="text-sm">
                                  <p className="font-medium text-foreground">This certificate doesn't line up</p>
                                  <ul className="mt-1 space-y-0.5 text-text-secondary">
                                    {certCheck.date_match === false && (
                                      <li>
                                        Classification date on the certificate ({certCheck.ocr?.classification_date || '—'}) doesn't match
                                        {certCheck.expected_source === 'api' ? ' the Udyam registry' : ' the date you entered'}
                                        {certCheck.expected_classification_date ? ` (${certCheck.expected_classification_date})` : ''}.
                                      </li>
                                    )}
                                    {certCheck.recent === false && (
                                      <li>{certCheck.outdated || 'This is not the current certificate. Please upload the current one.'}</li>
                                    )}
                                  </ul>
                                  <p className="mt-2 text-text-secondary">
                                    Upload a corrected certificate, or proceed as a non-MSME vendor by signing a declaration instead.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={proceedAsNonMsme}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-warning/50 bg-warning/10 px-3 py-1.5 text-sm font-medium text-foreground hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
                                  >
                                    <Icon name="FileSignature" size={14} /> Proceed as Non-MSME (sign declaration)
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {formData.msmeStatus === 'not-registered' && (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 mb-3">
                    <strong>Note:</strong> If you are not MSME registered, you need to generate a declaration document.
                  </p>
                  <button
                    type="button"
                    {...fieldProps('msmeDeclaration')}
                    onClick={() => setShowMSMEDeclaration(true)}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                  >
                    Generate the Declaration for Non-MSME
                  </button>
                </div>

                {errors.msmeDeclaration && !formData.msmeDeclaration && (
                  <p className="text-sm text-destructive">{errors.msmeDeclaration}</p>
                )}

                {formData.msmeDeclaration && (
                  <div className="text-sm text-success">
                    ✓ MSME Declaration PDF has been generated and downloaded
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Classifications */}
      {/* Additional Classifications removed as per requirement */}

      {/* MSME Declaration Modal */}
      {showMSMEDeclaration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Generate the Declaration for Non-MSME</h3>
            <p className="text-sm text-text-secondary mb-6">
              This will generate a declaration document stating that your company is not registered under MSME. 
              The PDF document will be automatically downloaded.
            </p>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={generateMSMEDeclarationPDF}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
              >
                Generate & Download
              </button>
              <button
                type="button"
                onClick={() => setShowMSMEDeclaration(false)}
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70 border border-border transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorizationStep;